// src/stores/auth.js
import { defineStore } from 'pinia';
import api from '@/api'; // your axios instance

// Helper: build an absolute URL for redirects if api.baseURL is relative
function buildAuthUrl(path = '') {
  const base = api?.defaults?.baseURL || '/api';
  // ensure single slash join
  if (base.startsWith('http')) return `${base.replace(/\/+$/, '')}${path}`;
  return `${base.replace(/\/+$/, '')}${path}`;
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    // Token is only used in "URL token" mode; cookie mode can keep this empty.
    token: localStorage.getItem('auth_token') || '',
    // User is the source of truth after hydrate()
    user: null, // { id, email, username, full_name, credit_balance, role_id, is_active, last_login_at }
    // Keep these for backward compatibility with the rest of your app:
    credits: Number(localStorage.getItem('credit_balance') || 0),
    permissions: JSON.parse(localStorage.getItem('permissions') || '[]'),
    role: localStorage.getItem('role') || '',
    loading: false,
    error: null,
  }),

  getters: {
    // Logged in if we have a user (works for both cookie & URL-token modes)
    loggedIn: (s) => !!s.user,
    displayName: (s) =>
      s.user?.full_name || s.user?.username || s.user?.email || 'User',
    lastLoginAt: (s) => s.user?.last_login_at || null,
    creditBalance: (s) =>
      Number(s.user?.credit_balance ?? s.credits ?? 0),
  },

  actions: {
    /** Initialize axios Authorization header from existing token (URL-token mode) */
    _syncAxiosAuthHeader() {
      if (this.token) {
        api.defaults.headers.common.Authorization = `Bearer ${this.token}`;
      } else {
        delete api.defaults.headers.common.Authorization;
      }
    },

    /** Persist token and update axios header (URL-token mode). */
    setToken(tok) {
      this.token = tok || '';
      if (this.token) {
        localStorage.setItem('auth_token', this.token);
      } else {
        localStorage.removeItem('auth_token');
      }
      this._syncAxiosAuthHeader();
    },

    /** Persist credits for legacy consumers; user remains the truthy source. */
    setCredits(amount) {
      const n = Number(amount) || 0;
      this.credits = n;
      localStorage.setItem('credit_balance', String(n));
      if (this.user) this.user.credit_balance = n;
    },

    setPermissions(perms = []) {
      this.permissions = Array.isArray(perms) ? perms : [];
      localStorage.setItem('permissions', JSON.stringify(this.permissions));
    },

    setRole(r = '') {
      this.role = r || '';
      localStorage.setItem('role', this.role);
      if (this.user) this.user.role_id = this.role; // if you map role_id to role string elsewhere, adjust as needed
    },

    /** Standard local login flow (username/password). */
    async login(credentials) {
      try {
        this.loading = true; this.error = null;
        const { data } = await api.post('/auth/login', credentials, { withCredentials: true });
        // Expecting { token?, credits, permissions, role }
        if (data?.token) this.setToken(data.token); // URL-token mode from local login (if your API returns it)
        if (data?.permissions) this.setPermissions(data.permissions);
        if (data?.role) this.setRole(data.role);
        if (typeof data?.credits !== 'undefined') this.setCredits(data.credits);
        // Finally, hydrate user profile from /api/me
        await this.hydrate();
      } catch (err) {
        const msg = err?.response?.data?.error || err.message || 'Login failed';
        this.error = msg;
        throw new Error(msg);
      } finally {
        this.loading = false;
      }
    },

    /** Start Google OAuth (browser will navigate). */
    beginGoogleLogin() {
      // Redirect user to backend starter endpoint
      window.location.href = buildAuthUrl('/auth/google');
    },

    /** Consume a token from /auth/callback?token=... (URL-token mode), then hydrate. */
    async consumeUrlTokenAndHydrate(token) {
      if (token) this.setToken(token);
      await this.hydrate();
    },

    /** Load current user via /api/me (works for both cookie mode & URL-token mode). */
    async hydrate() {
      // Ensure axios header is aligned with stored token if any
      this._syncAxiosAuthHeader();
      try {
        this.loading = true; this.error = null;
        const { data } = await api.get('/me', { withCredentials: true });
        this.user = data || null;
        // Keep legacy fields aligned for existing UI
        if (this.user) {
          this.setCredits(this.user.credit_balance ?? this.credits ?? 0);
          // Map role if your API returns role_id; adjust as per your app's role conventions
          if (typeof this.user.role_id !== 'undefined' && this.user.role_id !== this.role) {
            this.setRole(this.user.role_id);
          }
        } else {
          // No user → clear legacy fields but keep token only if you want to retry later
          this.setCredits(0);
        }
      } catch (err) {
        // If unauthorized, clear token in URL-token mode
        if (err?.response?.status === 401) this.setToken('');
        this.user = null;
        this.error = err?.response?.data?.error || 'Failed to load session';
      } finally {
        this.loading = false;
      }
    },

    /** Optional: refresh credits from /api/me without full hydrate (lightweight). */
    async refreshCredits() {
      try {
        const { data } = await api.get('/me', { withCredentials: true });
        if (data) this.setCredits(data.credit_balance ?? 0);
      } catch {
        /* ignore */
      }
    },

    /** Clear session (client-side). If you set cookies server-side, optionally hit /auth/logout. */
    async logout() {
      try {
        // Optional (no-op if not implemented on backend):
        // await api.post('/auth/logout', {}, { withCredentials: true });
      } catch { /* ignore */ }

      this.setToken('');
      this.user = null;
      this.setCredits(0);
      this.setPermissions([]);
      this.setRole('');

      // Clear any other persisted keys if needed
      localStorage.removeItem('credit_balance');
      localStorage.removeItem('permissions');
      localStorage.removeItem('role');
    },
  },
});
